
import Layout from "../../../component/layout/Layout";
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, Search, Menu, Zap, ImageIcon, BarChart3, ListOrdered, PanelsTopLeft, ShoppingCart
} from 'lucide-react';
import { getMyAccountStatus, type MomentAccountStatus } from '../api/momentApi';
import { getAccessToken } from '../../../util/tokenUtil';

interface PhoneMockupProps {
  children: ReactNode;
  label?: string;
  labelPosition?: string;
}

const PhoneMockup = ({ children, label, labelPosition }: PhoneMockupProps) => (
  <div className="tw-relative tw-w-[300px] tw-aspect-[9/19] tw-bg-slate-900 tw-rounded-[3rem] tw-border-[20px] tw-border-black tw-shadow-2xl tw-overflow-hidden tw-ring-[4px] tw-ring-black">
    <div className="tw-w-full tw-h-full tw-bg-[#8AA7C1] tw-flex tw-flex-col tw-relative">
      <div className="tw-h-12 tw-w-full tw-flex tw-items-center tw-justify-between tw-px-5 tw-border-b tw-border-slate-100/50">
        <ChevronLeft size={20} className="tw-text-slate-900" />
        <div className="tw-flex tw-flex-col tw-items-center">
          <div className="tw-font-bold tw-text-sm tw-text-slate-900">비즈톡</div>
          <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-600">
            1234-5678
            <ChevronDown size={12} className="tw-opacity-60" />
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-4">
          <Search size={20} className="tw-text-slate-900" />
          <Menu size={20} className="tw-text-slate-900" />
        </div>
      </div>
      <div className="tw-px-4 tw-h-full tw-overflow-y-auto">{children}</div>
    </div>
    {label && (
      <div className={`tw-absolute ${labelPosition} tw-bg-white tw-px-4 tw-py-2 tw-rounded-full tw-shadow-md tw-text-sm tw-font-bold tw-z-30`}>
        {label}
      </div>
    )}
  </div>
);

type FeatureCardType = "wide-image" | "wide-list" | "carousel-feed" | "carousel-commerce" | "analytics";

type FeatureCardItem = {
  type: FeatureCardType;
  title: string;
  description: string;
  iconBgClass: string;
};

type MiniCarouselItem = {
  title: string;
  subtitle: string;
  accent: string;
  meta?: string;
};

const FEATURE_CARD_ITEMS: FeatureCardItem[] = [
  {
    type: "wide-image",
    title: "와이드 이미지형",
    description: "신상품 출시나 프로모션 알림에 탁월합니다. 큰 사이즈의 이미지를 활용해 주목도 높은 메시지를 구성할 수 있습니다.",
    iconBgClass: "tw-bg-blue-600",
  },
  {
    type: "wide-list",
    title: "와이드 리스트형",
    description: "다양한 주제의 메시지를 리스트 유형으로 구성해보세요. 유저가 필요한 정보를 직접 선택하여 탐색할 수 있습니다.",
    iconBgClass: "tw-bg-purple-600",
  },
  {
    type: "carousel-feed",
    title: "캐러셀 피드형",
    description: "여러 장의 카드로 제품, 이벤트, 콘텐츠를 가볍게 넘겨보게 만들 수 있습니다. 카드 영역 안쪽을 좌우로 드래그해보세요.",
    iconBgClass: "tw-bg-sky-600",
  },
  {
    type: "carousel-commerce",
    title: "캐러셀 커머스형",
    description: "상품 정보와 가격을 한 번에 보여주는 쇼핑형 메시지입니다. 내부 미리보기에서 상품 카드 흐름을 직접 이동해볼 수 있습니다.",
    iconBgClass: "tw-bg-emerald-600",
  },
  {
    type: "analytics",
    title: "카카오 모먼트 성과분석",
    description: "메시지 발송 결과는 데이터로 받아보세요. 읽음부터 클릭까지 총 4개의 지표에 대해서 확인 가능합니다.",
    iconBgClass: "tw-bg-blue-500",
  },
];

const FEATURE_PAGE_SIZE = 3;

function getFeatureSlideCount() {
  return FEATURE_CARD_ITEMS.length;
}

function MiniCarouselStrip({
  variant,
}: {
  variant: "feed" | "commerce";
}) {
  const items: MiniCarouselItem[] =
    variant === "feed"
      ? [
          { title: "봄 신상 컬렉션", subtitle: "이번 주 추천 아이템", accent: "tw-bg-sky-400" },
          { title: "리뷰 이벤트 안내", subtitle: "참여 시 포인트 적립", accent: "tw-bg-indigo-400" },
          { title: "장바구니 리마인드", subtitle: "찜 상품 가격 확인", accent: "tw-bg-cyan-400" },
          { title: "멤버십 혜택", subtitle: "추가 할인 쿠폰 제공", accent: "tw-bg-blue-400" },
        ]
      : [
          { title: "린넨 셔츠", subtitle: "39,000원", meta: "59,000원 33%", accent: "tw-bg-emerald-400" },
          { title: "썸머 팬츠", subtitle: "29,000원", meta: "45,000원 35%", accent: "tw-bg-teal-400" },
          { title: "라이트 자켓", subtitle: "79,000원", meta: "99,000원 20%", accent: "tw-bg-green-400" },
          { title: "데일리 슈즈", subtitle: "49,000원", meta: "69,000원 28%", accent: "tw-bg-lime-400" },
        ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartXRef = useRef<number | null>(null);
  const stripWidthRef = useRef<HTMLDivElement | null>(null);

  const maxIndex = Math.max(0, items.length - 1);
  const cardWidthPercent = 76;
  const gapPercent = 4;
  const peekPercent = 10;
  const baseTranslate = activeIndex * (cardWidthPercent + gapPercent) - peekPercent;

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragStartXRef.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStartXRef.current === null) return;
    setDragOffset(event.clientX - dragStartXRef.current);
  };

  const finishDrag = () => {
    const width = stripWidthRef.current?.offsetWidth ?? 0;
    const threshold = Math.max(36, width * 0.12);

    if (dragOffset <= -threshold && activeIndex < maxIndex) {
      setActiveIndex((prev) => prev + 1);
    } else if (dragOffset >= threshold && activeIndex > 0) {
      setActiveIndex((prev) => prev - 1);
    }

    dragStartXRef.current = null;
    setDragOffset(0);
  };

  return (
    <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
      <div
        ref={stripWidthRef}
        className="tw-relative tw-overflow-hidden tw-select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
      >
        <div
          className="tw-flex tw-gap-[4%] tw-transition-transform tw-duration-300 tw-ease-out"
          style={{ transform: `translateX(calc(${-baseTranslate}% + ${dragOffset}px))` }}
        >
          {items.map((item) => (
            <div
              key={item.title}
              className="tw-w-[76%] tw-shrink-0 tw-rounded-[20px] tw-overflow-hidden tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm"
            >
              <div className={`tw-h-28 ${item.accent} tw-flex tw-items-center tw-justify-center tw-text-white tw-font-black tw-text-lg`}>
                {variant === "feed" ? "콘텐츠" : "상품"}
              </div>
              <div className="tw-p-4 tw-space-y-1">
                <p className="tw-text-sm tw-font-bold tw-text-slate-900">{item.title}</p>
                <p className={`tw-text-xs ${variant === "commerce" ? "tw-text-rose-500 tw-font-bold" : "tw-text-slate-500"}`}>
                  {item.subtitle}
                </p>
                {variant === "commerce" ? (
                  <p className="tw-text-[11px] tw-text-slate-400 tw-line-through">{item.meta}</p>
                ) : (
                  <p className="tw-text-[11px] tw-text-slate-400">좌우로 드래그해 다음 카드 보기</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ item }: { item: FeatureCardItem }) {
  const Icon =
    item.type === "wide-image"
      ? ImageIcon
      : item.type === "wide-list"
        ? ListOrdered
        : item.type === "carousel-feed"
          ? PanelsTopLeft
          : item.type === "carousel-commerce"
            ? ShoppingCart
            : BarChart3;

  if (item.type === "analytics") {
    return (
      <div className="tw-bg-[#1e293b] tw-rounded-[32px] tw-p-8 tw-shadow-2xl tw-relative tw-overflow-hidden tw-h-full">
        <div className={`tw-w-12 tw-h-12 ${item.iconBgClass} tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-mb-6`}>
          <Icon className="tw-text-white" size={24} />
        </div>
        <h3 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-4">{item.title}</h3>
        <p className="tw-text-slate-300 tw-leading-relaxed tw-mb-8 tw-font-medium">{item.description}</p>
        <div className="tw-bg-white/5 tw-backdrop-blur-sm tw-border tw-border-white/10 tw-rounded-2xl tw-p-6 tw-space-y-6">
          {[
            { label: "발송수", value: "1,240건", color: "tw-text-white", barColor: "tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-400", width: "tw-w-full" },
            { label: "열람수", value: "600", color: "tw-text-blue-400", barColor: "tw-bg-blue-500", width: "tw-w-[48%]" },
            { label: "전체 클릭수", value: "342", color: "tw-text-emerald-400", barColor: "tw-bg-emerald-400", width: "tw-w-[28%]" },
            { label: "발송 실패수", value: "5", color: "tw-text-rose-400", barColor: "tw-bg-rose-400", width: "tw-w-[2%]" },
          ].map((metric) => (
            <div key={metric.label} className="tw-space-y-2">
              <div className="tw-flex tw-justify-between tw-items-end">
                <p className="tw-text-slate-400 tw-text-xs tw-font-bold">{metric.label}</p>
                <p className={`tw-text-xl tw-font-black ${metric.color}`}>{metric.value}</p>
              </div>
              <div className="tw-w-full tw-h-1.5 tw-bg-white/10 tw-rounded-full tw-overflow-hidden">
                <div className={`${metric.width} tw-h-full ${metric.barColor} tw-rounded-full`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-bg-white tw-rounded-[32px] tw-p-8 tw-border tw-border-slate-200 tw-shadow-sm hover:tw-shadow-xl tw-transition-all tw-h-full">
      <div className={`tw-w-12 tw-h-12 ${item.iconBgClass} tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-mb-6`}>
        <Icon className="tw-text-white" size={24} />
      </div>
      <h3 className="tw-text-2xl tw-font-bold tw-text-slate-900 tw-mb-4">{item.title}</h3>
      <p className="tw-text-slate-600 tw-leading-relaxed tw-mb-8 tw-font-medium">{item.description}</p>

      {item.type === "wide-image" && (
        <div className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-border tw-border-slate-100 tw-aspect-video tw-bg-slate-900">
          <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/80 tw-via-transparent tw-to-transparent tw-p-5 tw-flex tw-flex-col tw-justify-end">
            <span className="tw-absolute tw-top-3 tw-right-3 tw-bg-black/40 tw-text-white tw-text-[10px] tw-px-2 tw-py-0.5 tw-rounded tw-backdrop-blur-sm">AD</span>
            <p className="tw-text-white tw-font-bold tw-text-lg tw-mb-1">26년 전용 프로모션 특가</p>
            <div className="tw-flex tw-justify-between tw-items-center">
              <p className="tw-text-blue-300 tw-text-sm">신상품 최대 50% 단독특가</p>
              <span className="tw-bg-white tw-text-black tw-text-[10px] tw-font-bold tw-px-3 tw-py-1 tw-rounded-full">지금 확인</span>
            </div>
          </div>
        </div>
      )}

      {item.type === "wide-list" && (
        <div className="tw-space-y-3 tw-p-4 tw-bg-slate-50 tw-rounded-2xl tw-border tw-border-slate-200">
          {[
            { n: "1", t: "이번 주 가장 사랑받은 신상", c: "트렌드 리포트" },
            { n: "2", t: "MD가 직접 고른 린넨 셔츠", c: "단독 20% 할인" },
            { n: "3", t: "당첨자 발표: 리뷰 이벤트", c: "지금 확인하기" },
          ].map((listItem) => (
            <div key={listItem.n} className="tw-flex tw-items-center tw-gap-3 tw-bg-white tw-p-3 tw-rounded-xl tw-shadow-sm tw-border tw-border-slate-100">
              <span className="tw-text-slate-900 tw-font-black tw-text-lg">{listItem.n}.</span>
              <div className="tw-flex-1">
                <p className="tw-text-slate-900 tw-text-xs tw-font-bold tw-mb-0.5">{listItem.t}</p>
                <p className="tw-text-slate-500 tw-text-[10px]">{listItem.c}</p>
              </div>
              <ChevronRight size={14} className="tw-text-slate-400" />
            </div>
          ))}
        </div>
      )}

      {item.type === "carousel-feed" && <MiniCarouselStrip variant="feed" />}
      {item.type === "carousel-commerce" && <MiniCarouselStrip variant="commerce" />}
    </div>
  );
}

function FeatureCarouselSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [trackIndex, setTrackIndex] = useState(getFeatureSlideCount());
  const [isAnimating, setIsAnimating] = useState(false);
  const [autoRotateTick, setAutoRotateTick] = useState(0);
  const isAnimatingRef = useRef(false);
  const slideCount = getFeatureSlideCount();
  const transitionMs = 520;
  const trackItems = [
    ...FEATURE_CARD_ITEMS,
    ...FEATURE_CARD_ITEMS,
    ...FEATURE_CARD_ITEMS,
  ];

  useEffect(() => {
    isAnimatingRef.current = isAnimating;
  }, [isAnimating]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!isAnimatingRef.current) {
        setActiveIndex((prev) => (prev + 1) % slideCount);
        setIsAnimating(true);
        setTrackIndex((prev) => prev + 1);
      }
    }, 5000);

    return () => window.clearInterval(timer);
  }, [autoRotateTick, isAnimating, slideCount]);

  useEffect(() => {
    if (!isAnimating) return;

    const timer = window.setTimeout(() => {
      let normalizedTrackIndex = trackIndex;

      if (trackIndex >= slideCount * 2) {
        normalizedTrackIndex = trackIndex - slideCount;
      } else if (trackIndex < slideCount) {
        normalizedTrackIndex = trackIndex + slideCount;
      }

      setIsAnimating(false);
      setTrackIndex(normalizedTrackIndex);
      setActiveIndex(normalizedTrackIndex % slideCount);
    }, transitionMs);

    return () => window.clearTimeout(timer);
  }, [isAnimating, slideCount, trackIndex]);

  const movePrev = () => {
    if (isAnimating) return;
    setAutoRotateTick((prev) => prev + 1);
    setActiveIndex((prev) => (prev - 1 + slideCount) % slideCount);
    setIsAnimating(true);
    setTrackIndex((prev) => prev - 1);
  };

  const moveNext = () => {
    if (isAnimating) return;
    setAutoRotateTick((prev) => prev + 1);
    setActiveIndex((prev) => (prev + 1) % slideCount);
    setIsAnimating(true);
    setTrackIndex((prev) => prev + 1);
  };

  return (
    <section className="tw-py-24 tw-bg-slate-50">
      <div className="tw-max-w-6xl tw-mx-auto tw-px-6">
        <div className="tw-text-center tw-mb-16">
          <h2 className="tw-text-blue-600 tw-font-bold tw-mb-3 tw-text-lg">Kakao Moment</h2>
          <p className="tw-text-4xl tw-font-black tw-text-slate-900 tw-tracking-tight">
            우리나라의 약 98.9%가 사용하는 카카오톡으로 광고메시지 보내기
          </p>
          <p className="tw-mt-5 tw-text-slate-700 tw-text-lg tw-font-medium tw-mb-0">
            카카오 모먼트 연동으로 비즈팅에서 보다 더 쉽게 카카오 광고메시지를 보내보세요!
          </p>
          <p className="tw-mt-0 tw-text-lg tw-font-medium tw-text-red-500">
            전화번호 없이 내 채널 친구에게 광고 메시지를 보낼 수 있습니다.
          </p>
          <p className="tw-mt-0 tw-mb-0 tw-text-center tw-text-xl tw-text-red-500 tw-font-bold">
            단가 정보: 카카오 모먼트 건당 단가: 15원(vat 별도)<br/>
            비즈팅 충전 포인트로 발송할 수 있습니다.
          </p>
        </div>
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mb-6">
          <button
            type="button"
            onClick={movePrev}
            className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 tw-shadow-sm hover:tw-bg-slate-50"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={moveNext}
            className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 tw-shadow-sm hover:tw-bg-slate-50"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="tw-relative tw-overflow-hidden">
          <div
            className="tw-flex tw-gap-8"
            style={{
              transform: `translateX(calc(-${trackIndex * 33.333333}% - ${trackIndex * (32 / 3)}px))`,
              transition: isAnimating ? `transform ${transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)` : "none",
            }}
          >
            {trackItems.map((item, index) => (
              <div
                key={`${index}-${item.type}`}
                className="tw-w-[calc((100%-64px)/3)] tw-shrink-0"
              >
                <FeatureCard item={item} />
              </div>
            ))}
          </div>
        </div>

        <div className="tw-mt-8 tw-flex tw-justify-center tw-gap-2">
          {Array.from({ length: slideCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                if (isAnimating) return;
                setAutoRotateTick((prev) => prev + 1);
                setActiveIndex(index);
                setIsAnimating(true);
                setTrackIndex(slideCount + index);
              }}
              className={`tw-border-0 tw-h-2.5 tw-rounded-full tw-transition-all ${
                activeIndex === index ? "tw-w-8 tw-bg-blue-600" : "tw-w-2.5 tw-bg-slate-300"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

const MomentIndex = () => {
  const navigate = useNavigate();
  const [account, setAccount] = useState<MomentAccountStatus | null | undefined>(undefined);

  useEffect(() => {
    if (!getAccessToken()) {
      setAccount(null);
      return;
    }
    getMyAccountStatus()
      .then((data) => setAccount(data ?? null))
      .catch(() => setAccount(null));
  }, []);

  const resolveMomentPath = (accountStatus: MomentAccountStatus | null) => {
    const isReady = accountStatus?.status === 'ACTIVE' && !!accountStatus.kakaoAdAccountId;
    return isReady ? '/moment/create' : '/moment/description';
  };

  const goToMoment = async () => {
    if (!getAccessToken()) { navigate('/account/login'); return; }
    if (account !== undefined) { navigate(resolveMomentPath(account)); return; }
    try {
      const data = await getMyAccountStatus();
      const nextAccount = data ?? null;
      setAccount(nextAccount);
      navigate(resolveMomentPath(nextAccount));
    } catch {
      setAccount(null);
      navigate('/moment/description');
    }
  };

  return (
    <Layout>

      {/* HERO */}
      <section className="tw-pt-32 tw-pb-40 tw-bg-gradient-to-br tw-from-white tw-to-blue-50 tw-px-6">
        <div className="tw-max-w-6xl tw-mx-auto tw-grid lg:tw-grid-cols-2 tw-gap-16 tw-items-center">
          <div>
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-rounded-full tw-bg-blue-100 tw-text-blue-700 tw-text-sm tw-font-bold tw-mb-6">
              <Zap size={14} />
              카카오 광고 메시지 발송
            </div>
            <h1 className="tw-text-5xl md:tw-text-6xl tw-font-bold tw-leading-tight tw-mb-6 tw-text-slate-900">
              <span className="tw-text-blue-600">내 채널 친구에게</span><br/> 카카오 광고 메시지를 보내보세요!
            </h1>
            <p className="tw-text-lg tw-text-slate-600 tw-mb-10 tw-leading-relaxed">
              문자 광고를 넘어 카카오 광고 메시지까지!
              비즈팅에서 한번에 해결하세요!
            </p>
            <div className="tw-flex tw-gap-4">
              <button onClick={goToMoment}
                className="tw-border-0 tw-px-8 tw-py-4 tw-bg-blue-600 tw-text-white tw-rounded-xl tw-font-bold tw-flex tw-items-center tw-gap-2 hover:tw-bg-blue-700">
                카카오 광고 메시지 보내기 <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div className="tw-relative tw-flex tw-justify-center tw-gap-6">
            <PhoneMockup>
              <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-p-4">
                <div className="tw-w-full tw-bg-white tw-rounded-2xl tw-overflow-hidden">
                  <div className="tw-h-40 tw-bg-blue-400 tw-flex tw-flex-col tw-items-center tw-justify-center">
                    <span className="tw-text-4xl tw-text-white tw-font-bold">30%</span>
                    <span className="tw-text-white tw-font-semibold">SUMMER SALE</span>
                  </div>
                  <div className="tw-p-4">
                    <button className="tw-border-0 tw-w-full tw-py-2 tw-bg-slate-100 tw-rounded-lg tw-text-sm tw-font-bold">
                      쿠폰 다운받기
                    </button>
                  </div>
                </div>
              </div>
              <div className="tw-mt-4 tw-bg-white tw-rounded-full tw-px-3 tw-py-1 tw-inline-block tw-text-[10px] tw-font-bold tw-text-purple-600 tw-shadow-sm">
                Type 1: 와이드 이미지형
              </div>
            </PhoneMockup>

            <PhoneMockup>
              <div className="tw-flex-1 tw-overflow-hidden tw-p-4 tw-mt-2">
                <div className="tw-bg-white tw-rounded-2xl tw-shadow-md tw-overflow-hidden">
                  <div className="tw-text-center tw-text-sm tw-font-semibold tw-py-3">회원님, 안녕하세요!</div>
                  <div className="tw-px-3">
                    <div className="tw-w-full tw-h-28 tw-bg-blue-400 tw-rounded-xl tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-white">
                      <div className="tw-text-3xl">🛍️</div>
                      <div className="tw-text-xs tw-font-semibold tw-mt-1 tw-text-center">
                        장바구니에 담겨진 상품이<br />구매를 기다리고 있어요!
                      </div>
                    </div>
                  </div>
                  <div className="tw-p-3 tw-space-y-3">
                    {[
                      { text: "최근 장바구니 상품", icon: "👜" },
                      { text: "장바구니 연관 상품", icon: "🎒" },
                      { text: "신상 아우터 할인", icon: "🧥" },
                    ].map((item, i) => (
                      <div key={i} className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-12 tw-h-12 tw-rounded-md tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                          <span className="tw-text-xl">{item.icon}</span>
                        </div>
                        <div className="tw-text-xs">{item.text}</div>
                      </div>
                    ))}
                  </div>
                  <div className="tw-p-3">
                    <button className="tw-border-0 tw-w-full tw-bg-gray-100 tw-text-sm tw-font-semibold tw-py-2 tw-rounded-lg">
                      상품 더 보기
                    </button>
                  </div>
                </div>
                <div className="tw-mt-4 tw-bg-white tw-rounded-full tw-px-3 tw-py-1 tw-inline-block tw-text-[10px] tw-font-bold tw-text-purple-600 tw-shadow-sm">
                  Type 2: 와이드 리스트형
                </div>
              </div>
            </PhoneMockup>
          </div>
        </div>
      </section>

      <FeatureCarouselSection />

      {/* CTA */}
      <section className="tw-py-32 tw-bg-white tw-text-center">
        <div className="tw-max-w-3xl tw-mx-auto tw-px-6">
          <h2 className="tw-text-3xl md:tw-text-4xl tw-font-black tw-mb-6 tw-text-slate-900 tw-leading-tight">
            일반 메시지부터 카카오 광고까지, <br />
            <span className="tw-text-blue-600">비즈팅 원-플랫폼(One-platform)</span>으로 통합 관리하세요.
          </h2>
          <p className="tw-text-slate-700 tw-text-lg tw-mb-10 tw-leading-relaxed tw-font-medium">
            여러 곳을 거칠 필요 없이 비즈팅 하나로 발송부터 성과 분석까지 한번에! <br className="tw-hidden md:tw-block" />
            <span className="tw-text-blue-600 tw-font-bold">비즈팅</span>에서 바로 시작해보세요
          </p>
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-justify-center">
            <button onClick={goToMoment}
              className="tw-border-0 tw-bg-blue-600 tw-text-white tw-px-10 tw-py-4 tw-rounded-xl tw-text-lg tw-font-bold hover:tw-bg-blue-700 tw-transition-all tw-shadow-xl tw-shadow-blue-200">
              카카오 광고 메시지 보내기
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MomentIndex;
